import { useMemo } from "react";
import { useHistory } from "../../../providers/HistoryProvider";
import { MultiDocPicker } from "../../documents/common/multidoctabs/MultiDocPicker";
import { TemplateObject } from "../../../utils/TemplateObject";
import TemplatePicker from "../../templates/TemplatePicker";
import { TemplateType } from "@neum/frontend-shared/constants/templateData";
import { isMobileDevice } from "../../../utils/deviceUtils";
import { RecordingUploadMode } from "../../../utils/UploadUtils";
import { v4 as uuidv4 } from "uuid";
import { ArrowRightIcon } from "@heroicons/react/24/solid";

interface UploadRecordingSelectNotePageProps {
    selectedNoteId: string;
    setSelectedNoteId: (documentId: string) => void;
    template: TemplateObject | undefined;
    setTemplate: (template: TemplateObject | undefined) => void;
    onProceed: () => void;
    mode: RecordingUploadMode;
    fixedMode: boolean;
    setMode: (mode: RecordingUploadMode) => void;
}

export const UploadRecordingSelectNotePage: React.FC<
    UploadRecordingSelectNotePageProps
> = ({
    selectedNoteId,
    setSelectedNoteId,
    template,
    setTemplate,
    onProceed,
    mode,
    fixedMode,
    setMode,
}: UploadRecordingSelectNotePageProps) => {
    const { allNotesMetadata, activeSession } = useHistory();

    const isMobile = useMemo(isMobileDevice, []);

    function onUploadClicked() {
        if (mode === RecordingUploadMode.CREATE_NEW_NOTE) {
            setSelectedNoteId(uuidv4())    
        }

        onProceed()
    }

    return (
        <div className="min-h-36">
            {!fixedMode && <div className="flex flex-row mb-4 justify-between">
                <button
                    className={`w-full px-4 py-2 rounded-l-full rounded-r-md text-sm transition-colors duration-200 ring-1 
                        ${
                            mode === RecordingUploadMode.ADD_TO_EXISTING_NOTE
                                ? "bg-main-button text-main-button-text hover:bg-main-button-hover ring-main-button"
                                : "bg-gray-200 hover:bg-gray-300 ring-gray-300"
                        }`}
                    onClick={() => setMode(RecordingUploadMode.ADD_TO_EXISTING_NOTE)}
                >
                    Add to existing note
                </button>
                <button
                    className={`w-full px-4 py-2 rounded-l-md rounded-r-full text-sm transition-colors duration-200 ring-1
                        ${
                            mode === RecordingUploadMode.CREATE_NEW_NOTE
                                ? "bg-main-button text-main-button-text hover:opacity-90 ring-main-button"
                                : "bg-gray-200 hover:bg-gray-300 ring-gray-300"
                        }`}
                    onClick={() => setMode(RecordingUploadMode.CREATE_NEW_NOTE)}
                >
                    Create new note
                </button>
            </div>}

            {mode === RecordingUploadMode.ADD_TO_EXISTING_NOTE && allNotesMetadata && selectedNoteId && (
                <div className="flex flex-col gap-y-3 items-center">
                    <span className="text-sm text-gray-500 block">
                        Which note do you want to add this recording to?
                    </span>
                    <MultiDocPicker
                        pickerLabel="Existing note"
                        activeDocumentId={selectedNoteId}
                        sessionDocumentsMetadata={allNotesMetadata}
                        onSelectionChange={async (documentId) => {
                            setSelectedNoteId(documentId);
                            return Promise.resolve();
                        }}
                    />
                </div>
            )}

            {mode === RecordingUploadMode.CREATE_NEW_NOTE && (
                <div className="flex flex-col gap-y-3 items-center">
                    <span className="text-sm text-gray-500 block">
                        Which template to generate the new note with?
                    </span>
                    <TemplatePicker
                        template={template}
                        templateIdForce={template ? `${template.organization}/${template.id}` : undefined}
                        setTemplate={setTemplate}
                        className={""}
                        pickerWidth={"w-full"}
                        templateBackground={"bg-white"}
                        type={TemplateType.MEDICAL}
                        pickerDirection={isMobile ? "above" : "below"}
                        externalTemplateId={
                            activeSession?.external_template_id
                        }
                    />
                </div>
            )}

            <button
                type="button"
                className="inline-flex w-full justify-center items-center rounded-md bg-main-button px-3 py-2 text-sm font-semibold text-main-button-text shadow-sm hover:bg-main-button-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 sm:col-start-2 mt-4"
                onClick={onUploadClicked}
            >
                Upload recording
                <ArrowRightIcon className="h-5 w-5 ml-2" aria-hidden="true" />
            </button>
        </div>
    );
};
